import { useMemo } from "react";

import { useServiceLabel, useSetServiceLabel } from "@/api/local/labels";
import type { Pool, Replica, Service } from "@/api/types";
import { GLOBAL_FOCUS, type Focus } from "@/workloads/focus";
import { SERVICE_TABS } from "@/workloads/scope";

import { DetailShell } from "./DetailShell";
import { ServiceAutoscaling } from "./service/ServiceAutoscaling";
import { ServiceCost } from "./service/ServiceCost";
import { ServiceDashboard } from "./service/ServiceDashboard";
import { ServiceEvents } from "./service/ServiceEvents";
import { ServiceInsights } from "./service/ServiceInsights";
import { ServiceProviders } from "./service/ServiceProviders";
import { ServiceRouting } from "./service/ServiceRouting";
import { ServiceSettings } from "./service/ServiceSettings";
import { ServiceWorkload } from "./service/ServiceWorkload";

interface Props {
  service: Service;
  pools: Pool[];
  replicas: Replica[];
  tab: string;
  setFocusAndTab: (focus: Focus, tab: string) => void;
}

export function ServiceDetail({
  service,
  pools,
  replicas,
  tab,
  setFocusAndTab,
}: Props) {
  const servicePools = useMemo(
    () => pools.filter((p) => p.service_id === service.id),
    [pools, service.id],
  );
  const serviceReplicas = useMemo(() => {
    const allowed = new Set(servicePools.map((p) => p.id));
    return replicas.filter((r) => allowed.has(r.pool_id));
  }, [servicePools, replicas]);
  const displayName = useServiceLabel(service.id, service.name);
  const setServiceLabel = useSetServiceLabel();

  return (
    <DetailShell
      tabs={SERVICE_TABS}
      activeTab={tab}
      onTabChange={(id) =>
        setFocusAndTab({ type: "service", id: service.id }, id)
      }
      navTitle={displayName}
      navSubtitle="service"
      onRenameTitle={(next) => {
        void setServiceLabel(service.id, next);
      }}
      renameHint={`slug stays "${service.name}"`}
      breadcrumb={[
        {
          label: "BackStage",
          onClick: () => setFocusAndTab(GLOBAL_FOCUS, "dashboard"),
        },
        { label: displayName },
      ]}
    >
      {renderTab({
        tab,
        service,
        servicePools,
        serviceReplicas,
        setFocusAndTab,
      })}
    </DetailShell>
  );
}

function renderTab({
  tab,
  service,
  servicePools,
  serviceReplicas,
  setFocusAndTab,
}: {
  tab: string;
  service: Service;
  servicePools: Pool[];
  serviceReplicas: Replica[];
  setFocusAndTab: (focus: Focus, tab: string) => void;
}) {
  switch (tab) {
    case "workload":
      return (
        <ServiceWorkload
          service={service}
          pools={servicePools}
          replicas={serviceReplicas}
        />
      );
    case "routing":
      return <ServiceRouting service={service} pools={servicePools} />;
    case "autoscaling":
      return <ServiceAutoscaling service={service} pools={servicePools} />;
    case "insights":
      return <ServiceInsights service={service} />;
    case "cost":
      return <ServiceCost service={service} pools={servicePools} />;
    case "providers":
      return <ServiceProviders service={service} />;
    case "events":
      return <ServiceEvents service={service} />;
    case "settings":
      return (
        <ServiceSettings
          service={service}
          pools={servicePools}
          replicas={serviceReplicas}
          setFocusAndTab={setFocusAndTab}
        />
      );
    case "dashboard":
    default:
      return (
        <ServiceDashboard
          service={service}
          pools={servicePools}
          replicas={serviceReplicas}
        />
      );
  }
}
